import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SchoolPropertiesForm, {
  emptySchoolForm,
  schoolToFormData,
  schoolFormToPayload,
  readFileAsDataURL,
  MAX_LOGO_BYTES
} from './SchoolPropertiesForm';

// School-scoped "/admin/:schoolId/settings" — lets a SchoolAdmin (or
// FullAdmin) edit their own school's properties (price, Venmo, grade
// options, logo, etc.) without needing the FullAdmin-only /admin/main
// area. Creating/deleting schools stays FullAdmin-only (see Schools.js) —
// this page never shows or lets anyone edit the school id, and always
// PUTs to the school-scoped alias (backend/main.go's
// "schoolAdminAPI.PUT("")" -> handlers.UpdateSchool), which
// RequireSchoolAccess restricts to schools the caller is actually
// assigned to.
function SchoolSettings() {
  const { schoolId } = useParams();
  const apiBase = `/api/admin/${schoolId}`;

  const [formData, setFormData] = useState(emptySchoolForm);
  const [gradeInput, setGradeInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const fetchSchool = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(apiBase);
      setFormData(schoolToFormData(response.data));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load school settings');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchSchool();
  }, [fetchSchool]);

  const addGrade = () => {
    const g = gradeInput.trim();
    if (g && !formData.grade_options.includes(g)) {
      setFormData(prev => ({ ...prev, grade_options: [...prev.grade_options, g] }));
    }
    setGradeInput('');
  };

  const removeGrade = (g) => {
    setFormData(prev => ({ ...prev, grade_options: prev.grade_options.filter(x => x !== g) }));
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      setError(`Logo image is too large (max ${Math.round(MAX_LOGO_BYTES / 1024)}KB)`);
      e.target.value = '';
      return;
    }
    try {
      const dataUrl = await readFileAsDataURL(file);
      const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
      setFormData(prev => ({
        ...prev,
        logoPreview: dataUrl,
        logoBase64: base64,
        logoContentType: file.type,
        logoRemoved: false
      }));
      setError('');
    } catch (err) {
      setError('Failed to read the selected image file');
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logoPreview: '', logoBase64: '', logoContentType: '', logoRemoved: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    try {
      const response = await axios.put(apiBase, schoolFormToPayload(formData));
      setFormData(schoolToFormData(response.data));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save school settings');
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', color: '#666' }}>Loading...</div>;
  }

  return (
    <div>
      <h2 style={{ marginBottom: '1.5rem' }}>School Settings</h2>

      {error && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {saved && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          Settings saved.
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '1.5rem',
        maxWidth: '520px'
      }}>
        <form onSubmit={handleSubmit}>
          <SchoolPropertiesForm
            formData={formData}
            setFormData={setFormData}
            showIdField={false}
            gradeInput={gradeInput}
            setGradeInput={setGradeInput}
            onAddGrade={addGrade}
            onRemoveGrade={removeGrade}
            onLogoChange={handleLogoChange}
            onRemoveLogo={handleRemoveLogo}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              style={{ padding: '0.5rem 1.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SchoolSettings;
